import clsx from "clsx";
import { ChevronDown, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

export interface Country {
    iso: string;
    name: string;
    dial: string;
    flag: string;
}

// ponytail: fixed ~15-country list; upgrade to full libphonenumber data when needed.
export const COUNTRIES: Country[] = [
    { iso: "ID", name: "Indonesia", dial: "+62", flag: "🇮🇩" },
    { iso: "MY", name: "Malaysia", dial: "+60", flag: "🇲🇾" },
    { iso: "SG", name: "Singapore", dial: "+65", flag: "🇸🇬" },
    { iso: "PH", name: "Philippines", dial: "+63", flag: "🇵🇭" },
    { iso: "TH", name: "Thailand", dial: "+66", flag: "🇹🇭" },
    { iso: "VN", name: "Vietnam", dial: "+84", flag: "🇻🇳" },
    { iso: "AU", name: "Australia", dial: "+61", flag: "🇦🇺" },
    { iso: "IN", name: "India", dial: "+91", flag: "🇮🇳" },
    { iso: "CN", name: "China", dial: "+86", flag: "🇨🇳" },
    { iso: "JP", name: "Japan", dial: "+81", flag: "🇯🇵" },
    { iso: "KR", name: "South Korea", dial: "+82", flag: "🇰🇷" },
    { iso: "AE", name: "UAE", dial: "+971", flag: "🇦🇪" },
    { iso: "SA", name: "Saudi Arabia", dial: "+966", flag: "🇸🇦" },
    { iso: "GB", name: "United Kingdom", dial: "+44", flag: "🇬🇧" },
    { iso: "US", name: "United States", dial: "+1", flag: "🇺🇸" },
];

interface PhoneInputProps {
    national: string;
    dial: string;
    onNationalChange: (value: string) => void;
    onDialChange: (dial: string) => void;
    placeholder?: string;
    autoFocus?: boolean;
    required?: boolean;
    disabled?: boolean;
}

export function PhoneInput({
    national,
    dial,
    onNationalChange,
    onDialChange,
    placeholder,
    autoFocus,
    required,
    disabled,
}: PhoneInputProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [unlocked, setUnlocked] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const active = COUNTRIES.find((c) => c.dial === dial) ?? COUNTRIES[0];

    useEffect(() => {
        if (!open) return;
        const close = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [open ]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return COUNTRIES;
        return COUNTRIES.filter(
            (c) =>
                c.name.toLowerCase().includes(q) ||
                c.dial.includes(q) ||
                c.iso.toLowerCase().includes(q),
        );
    }, [query]);

    return (
        <div ref={rootRef} className="relative">
            <div
                className={clsx(
                    "app-shell-border app-content-subtle flex w-full items-stretch overflow-visible rounded-xl border transition-colors",
                    "focus-within:border-[var(--color-primary)] focus-within:ring-2 focus-within:ring-[var(--color-primary)]/25",
                    disabled && "opacity-60",
                )}
            >
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setOpen((v) => !v)}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    className="app-shell-border app-text-primary flex shrink-0 items-center gap-1.5 border-r px-3 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5"
                >
                    <span className="text-base leading-none">{active.flag}</span>
                    <span>{active.dial}</span>
                    <ChevronDown className="h-4 w-4 opacity-60" />
                </button>
                <input
                    type="tel"
                    inputMode="tel"
                    name="phone-national-flexv-nofill"
                    autoComplete="new-password"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    data-1p-ignore
                    data-lpignore="true"
                    data-bwignore
                    value={national}
                    readOnly={!unlocked}
                    onFocus={() => setUnlocked(true)}
                    onChange={(e) => onNationalChange(e.target.value.replace(/[^\d\s-]/g, ""))}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    required={required}
                    disabled={disabled}
                    className="app-text-primary min-w-0 flex-1 bg-transparent px-3.5 py-2.5 text-sm outline-none placeholder:text-[var(--app-text-soft)]"
                />
                {national && !disabled && (
                    <button
                        type="button"
                        aria-label="Hapus nomor HP"
                        onClick={() => onNationalChange("")}
                        className="app-text-soft flex shrink-0 items-center pr-3 transition-opacity hover:opacity-70"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            {open && (
                <div className="app-content-card absolute left-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-xl border shadow-lg">
                    <div className="app-shell-border flex items-center gap-2 border-b px-3 py-2">
                        <Search className="h-4 w-4 opacity-50" />
                        <input
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Cari negara / kode…"
                            className="app-text-primary w-full bg-transparent text-sm outline-none placeholder:text-[var(--app-text-soft)]"
                        />
                    </div>
                    <ul role="listbox" className="max-h-60 overflow-y-auto py-1">
                        {filtered.map((c) => (
                            <li key={c.iso}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={c.dial === dial}
                                    onClick={() => {
                                        onDialChange(c.dial);
                                        setOpen(false);
                                        setQuery("");
                                    }}
                                    className={clsx(
                                        "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-black/5 dark:hover:bg-white/5",
                                        c.dial === dial && "font-semibold text-[var(--color-primary)]",
                                    )}
                                >
                                    <span className="text-base leading-none">{c.flag}</span>
                                    <span className="app-text-primary flex-1 truncate">{c.name}</span>
                                    <span className="app-text-soft">{c.dial}</span>
                                </button>
                            </li>
                        ))}
                        {filtered.length === 0 && (
                            <li className="app-text-soft px-3 py-4 text-center text-sm">
                                Negara tidak ditemukan
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
